/**
 * Client for the `knowledge-base` edge function (design §4, step 3 §3):
 * knowledge bases, their files and their descriptions. Dashboard users cannot
 * write the kb_* tables (migration 20261003160000), so everything goes through
 * it. Field names are the column names. Keep in sync with
 * `supabase/functions/knowledge-base/` and the `utils/kb-*.ts` rule mirrors.
 *
 * An upload is two requests around a resumable upload (`uploadFile`): a slot
 * is reserved (for Replace, in a ready file's place), the browser sends the
 * file to Storage in 6 MB pieces through the slot's resumable link (TUS), and
 * the confirm hands it to the worker, which reads, cuts and embeds it.
 */

import { api, ApiError } from "@/lib/api-client";
import { sendFile } from "@/lib/resumable-upload";

const BASE = "/knowledge-base";

/** What to tell the customer: the function's own sentence, or `fallback` when the request never got an answer. */
export const failureMessage = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

/** A `kb_stores` row as the function returns it, with its files' counts. */
export interface KnowledgeBase {
  id: string;
  workspace_id: string;
  agent_id: string | null;
  display_name: string;
  tool_description: string | null;
  /** `manual` once someone wrote the description by hand: uploads no longer rewrite it. */
  tool_description_source: "generated" | "manual";
  /** A hand-written description whose files changed since: the page asks whether to rewrite it. */
  tool_description_review: boolean;
  metadata: unknown;
  created_at: string;
  updated_at: string;
  /** Every file but an upload slot whose link has expired. */
  file_count: number;
  ready_count: number;
  processing_count: number;
  error_count: number;
  /** Of the ready and processing files. */
  chunk_count: number;
}

export type FileStatus = "uploading" | "processing" | "ready" | "error";

/** A `kb_documents` row: every column but the worker's own. */
export interface KnowledgeBaseFile {
  id: string;
  store_id: string;
  display_name: string;
  /** Where the file sits in Storage. */
  file_name: string;
  /** The content type it was uploaded with. */
  file_type: string;
  file_size: number;
  status: FileStatus;
  error_code: string | null;
  /** Why it failed, in the customer's words. */
  error_message: string | null;
  chunk_count: number;
  /** Of `chunk_count`, the passages saved so far while it is processed. */
  chunks_saved: number;
  content_hash: string | null;
  upload_expires_at: string | null;
  /** The extracted text's length; null for a file from before step 2. */
  text_chars: number | null;
  attempts: number;
  processed_at: string | null;
  /** The ready file this one takes the place of once it is ready itself; null for any other file. */
  replaces_id: string | null;
  /** What could not be read from a ready file, in the customer's words (a page that is only a picture);
   * absent from a function deployed before 2026-10-20. */
  warnings?: string[];
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

/** A `kb_plan_limits` row: what the workspace's plan allows. */
export interface KnowledgeBaseLimits {
  max_file_bytes: number;
  /** HTML, XML and YAML files: they are read whole, so they may be smaller. */
  max_markup_file_bytes: number;
  max_file_chunks: number;
  max_files_per_kb: number;
  max_chunks_per_kb: number;
  max_kbs: number;
  max_storage_bytes: number;
  /** Null: no workspace-wide cap. */
  max_chunks_per_workspace: number | null;
  max_chunks_per_month: number;
}

/** `GET /knowledge-base/usage`: the plan, its limits, and what the workspace uses of them. */
export interface KnowledgeBaseUsage {
  plan: "free" | "pro";
  limits: KnowledgeBaseLimits;
  knowledge_bases: number;
  storage_bytes: number;
  chunks: number;
  /** The month the chunks count toward, "YYYY-MM-01" (UTC). */
  month: string;
  month_chunks: number;
  /** Extension -> content type. */
  file_types: Record<string, string>;
  /** The extensions held to `max_markup_file_bytes`. */
  markup_extensions: string[];
}

/** A reserved slot and where the browser sends the file: the resumable link, and the object it fills. */
export interface ReservedUpload {
  file: KnowledgeBaseFile;
  upload: {
    signed_url: string;
    path: string;
    /** The upload's only credential. */
    token: string;
    content_type: string;
    bucket: string;
    resumable_url: string;
  };
}

/** A page of a ready file's passages: the text its agents search, as they read it. */
export interface PassagePage {
  file_id: string;
  /** From 1. */
  page: number;
  page_size: number;
  /** The file's passages, on every page. */
  total: number;
  passages: { index: number; content: string }[];
}

/** A batch's Retry: the files that run again, as they now are, and why any other may not. */
export interface RetriedFiles {
  files: KnowledgeBaseFile[];
  refused: { id: string; message: string }[];
}

/** A workspace's knowledge bases, newest first. */
export const listKnowledgeBases = (workspaceId: string) =>
  api.getList<KnowledgeBase>(BASE, { query: { workspace_id: workspaceId } });

export const getUsage = (workspaceId: string) =>
  api.getItem<KnowledgeBaseUsage>(`${BASE}/usage`, { query: { workspace_id: workspaceId } });

/** A plan with no room for another is refused (409) with the plan's sentence. */
export const createKnowledgeBase = (workspaceId: string, displayName: string) =>
  api.post<KnowledgeBase>(BASE, { workspace_id: workspaceId, display_name: displayName });

/** Its files go too. */
export const deleteKnowledgeBase = (id: string) => api.del(`${BASE}/${id}`);

/** A hand edit; `null` clears the description and the bot then uses its generic one. */
export const updateKnowledgeBaseDescription = (id: string, description: string | null) =>
  api.put<KnowledgeBase>(`${BASE}/${id}`, { tool_description: description });

/** "Keep mine": the hand-written description stays and the question goes away. */
export const keepDescription = (id: string) =>
  api.put<KnowledgeBase>(`${BASE}/${id}`, { tool_description_review: false });

/** The description, written again from the ready files now. */
export const writeDescription = (id: string) => api.post<KnowledgeBase>(`${BASE}/${id}/description`, {});

/** A knowledge base's files, newest first. */
export const listFiles = (id: string) => api.getList<KnowledgeBaseFile>(`${BASE}/${id}/files`);

/** What a slot is reserved for: the file's name and size, and the SHA-256 of its bytes. */
type FileToReserve = { file_name: string; file_size: number; content_hash: string };

export const reserveUpload = (id: string, file: FileToReserve) => api.post<ReservedUpload>(`${BASE}/${id}/files`, file);

/** A slot for a new file in a ready one's place: the old one is searched until the new one is ready. */
export const reserveReplacement = (fileId: string, file: FileToReserve) =>
  api.post<ReservedUpload>(`${BASE}/files/${fileId}/replace`, file);

export const confirmUpload = (fileId: string) => api.post<KnowledgeBaseFile>(`${BASE}/files/${fileId}/confirm`, {});

/** A failed file, processed again. */
export const retryFile = (fileId: string) => api.post<KnowledgeBaseFile>(`${BASE}/files/${fileId}/retry`, {});

/** Delete, or Cancel for an unfinished upload. */
export const deleteFile = (fileId: string) => api.del(`${BASE}/files/${fileId}`);

/** Several of a knowledge base's files at once (up to 25): all of them go, or none. */
export const deleteFiles = (id: string, fileIds: string[]) =>
  api.post<{ deleted: string[] }>(`${BASE}/${id}/files/delete`, { file_ids: fileIds });

/** Several failed files of a knowledge base (up to 25), each processed again unless it is refused. */
export const retryFiles = (id: string, fileIds: string[]) =>
  api.post<RetriedFiles>(`${BASE}/${id}/files/retry`, { file_ids: fileIds });

/** A page (from 1) of a ready file's passages, 20 a page. */
export const listPassages = (fileId: string, page: number) =>
  api.getItem<PassagePage>(`${BASE}/files/${fileId}/passages`, { query: { page: String(page) } });

/** A link to the stored file, good for 60 s, that downloads it under its name. */
export const getDownloadLink = (fileId: string) =>
  api.getItem<{ url: string; expires_in: number }>(`${BASE}/files/${fileId}/download`);

export const TEXT_NOT_LOADED = "The text could not be loaded. Try again in a moment.";

/** A text file's text, read through its download link: what Edit opens. */
export async function getFileText(fileId: string): Promise<string> {
  const { url } = await getDownloadLink(fileId);
  const response = await fetch(url).catch(() => null);
  if (!response?.ok) throw new Error(TEXT_NOT_LOADED);
  return await response.text();
}

/** A test search's passage, and what the agent's search does with it (the function's search.ts). */
export interface SearchedPassage {
  rank: number;
  file: string;
  content: string;
  score: number;
  verdict: "sent" | "past_top_k" | "below_cutoff" | "far_from_best";
}

/** The test console's answer: the rule it ran and the five closest passages, best first. */
export interface TestSearch {
  query: string;
  top_k: number;
  min_similarity: number;
  close_to_best: number;
  passages: SearchedPassage[];
}

/** What the test console asks: a question of these knowledge bases, under an agent's rule (the defaults when none). */
export interface TestSearchRequest {
  workspace_id: string;
  store_ids: string[];
  query: string;
  top_k?: number;
  min_similarity?: number;
}

/** The passages an agent's search would get for `query`, and the ones it would not. */
export const testSearch = (request: TestSearchRequest) => api.post<TestSearch>(`${BASE}/search`, request);

/** What the function checks a file's bytes against: their SHA-256, as lowercase hex. */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * One file, start to finish, with the SHA-256 the dialog read from it (its
 * duplicate check came first): into the knowledge base, or, with
 * `replacesId`, in that ready file's place. A refusal at any step rejects with
 * the function's sentence. `onProgress` hears of the slot as soon as it exists
 * (at 0%), then of every step of the upload, and 100% once it has finished
 * and the confirm is on its way. An upload that fails gives the slot back, so
 * it does not hold the plan's room until its link expires.
 */
export async function uploadFile(
  storeId: string,
  file: File,
  contentHash: string,
  onProgress: (fileId: string, percent: number) => void,
  replacesId?: string,
): Promise<KnowledgeBaseFile> {
  const request = { file_name: file.name, file_size: file.size, content_hash: contentHash };
  const reserved = await (replacesId ? reserveReplacement(replacesId, request) : reserveUpload(storeId, request));
  const fileId = reserved.file.id;
  onProgress(fileId, 0);
  try {
    await sendFile(reserved.upload, file, (percent) => onProgress(fileId, percent));
  } catch (error) {
    await deleteFile(fileId).catch(() => undefined);
    throw error;
  }
  onProgress(fileId, 100);
  return await confirmUpload(fileId);
}
